import { createOpenAPIError } from "@fleet/utils";
import type { AdminFontRow, AdminThemeRow } from "@fleet/types";

import type { components } from "@custodia/client";

export type S3ObjectDTO = components["schemas"]["S3ObjectDTO"];

export interface S3Object extends S3ObjectDTO {
  extension?: string;
}

/**
 * Enriches a raw S3ObjectDTO with parsed filename and type.
 */
export function enrichS3Object(obj: S3ObjectDTO): S3Object {
  const parts = obj.key.split("/");
  const filename = parts.at(-1) ?? obj.key;
  const isWoff2 = filename.endsWith(".woff2");
  const isTtf = filename.endsWith(".ttf");
  const extension = filename.split(".").at(-1);

  let type: S3Object["type"] = "unknown";
  if (isTtf) {
    type = "ttf";
  } else if (isWoff2) {
    type = "woff2";
  }

  return {
    ...obj,
    filename,
    extension,
    type,
  };
}

export type StorageFilter = "all" | "orphaned" | "linked";

/**
 * Agnostic logic for Admin Storage management.
 */
export function getAdminStorageHandlers(options: {
  objects: S3ObjectDTO[];
  onRefetch: () => void;
  onPrune: () => void;
}) {
  const { objects: rawObjects, onRefetch, onPrune } = options;
  const objects = rawObjects.map(enrichS3Object);

  const getStats = () => {
    const orphanedCount = objects.filter((o) => o.isOrphaned).length;
    const totalSize = objects.reduce((acc, o) => acc + o.size, 0);
    const linkedCount = objects.length - orphanedCount;

    return {
      totalCount: objects.length,
      orphanedCount,
      linkedCount,
      totalSize,
      // Helper for labels
      getLabelCount: (filter: StorageFilter) => {
        if (filter === "orphaned") {
          return orphanedCount;
        }
        if (filter === "linked") {
          return linkedCount;
        }
        return objects.length;
      },
    };
  };

  const getFilteredObjects = (filter: StorageFilter) =>
    objects.filter((o) => {
      if (filter === "orphaned") {
        return o.isOrphaned;
      }
      if (filter === "linked") {
        return !o.isOrphaned;
      }
      return true;
    });

  return {
    objects,
    getStats,
    getFilteredObjects,
    handleRefresh: onRefetch,
    handlePrune: onPrune,
  };
}

/**
 * Provides a human-readable summary of a variant's health.
 */
export function getVariantHealthSummary(variant: {
  exists: boolean;
  size?: number | null;
  ok?: boolean;
  contentTypeOk?: boolean;
}) {
  if (!variant.exists) {
    return "missing";
  }
  const isOk = variant.ok ?? variant.contentTypeOk;
  return isOk ? "ok" : "wrong content-type";
}

export interface ThemeAuditActions<TSingle, TAll> {
  auditSingle: (id: string) => Promise<{ data: TSingle | null; error: unknown }>;
  auditAll: () => Promise<{ data: TAll | null; error: unknown }>;
  repairPairs: () => Promise<{
    data: components["schemas"]["ThemeAdminSuccessResponse"] | null;
    error: unknown;
  }>;
}

export function getAdminThemeAuditHandlers<TSingle, TAll>(options: {
  actions: ThemeAuditActions<TSingle, TAll>;
  setSingleResult: (data: TSingle | null) => void;
  setAllResult: (data: TAll | null) => void;
  setIsAuditing: (isAuditing: boolean) => void;
  setIsRepairing?: (isRepairing: boolean) => void;
  onRepairSuccess: (result: { clearedCount: number; repairedCount: number }) => void;
  onRepairError: (error: unknown) => void;
  onAuditError?: (error: Error) => void;
}) {
  const {
    actions,
    setSingleResult,
    setAllResult,
    setIsAuditing,
    setIsRepairing,
    onRepairSuccess,
    onRepairError,
    onAuditError,
  } = options;

  const runSingle = async (id: string) => {
    setIsAuditing(true);
    try {
      const { data, error } = await actions.auditSingle(id);
      if (error) {
        console.error("Audit error:", error);
        onAuditError?.(createOpenAPIError(error));
        return;
      }
      if (data) {
        setSingleResult(data);
        setAllResult(null);
      }
    } finally {
      setIsAuditing(false);
    }
  };

  const runAll = async () => {
    setIsAuditing(true);
    try {
      const { data, error } = await actions.auditAll();
      if (error) {
        console.error("Audit all error:", error);
        onAuditError?.(createOpenAPIError(error));
        return;
      }
      if (data) {
        setAllResult(data);
        setSingleResult(null);
      }
    } finally {
      setIsAuditing(false);
    }
  };

  const runRepair = async () => {
    setIsRepairing?.(true);
    try {
      const { data, error } = await actions.repairPairs();
      if (error) {
        onRepairError(error);
      } else if (data) {
        onRepairSuccess({
          clearedCount: data.clearedCount ?? 0,
          repairedCount: data.repairedCount ?? 0,
        });
      }
    } finally {
      setIsRepairing?.(false);
    }
  };

  return {
    runSingle,
    runAll,
    runRepair,
  };
}

/**
 * Handles logic for pairing themes (dark/light pairs).
 */
export function getThemePairingHandlers(allThemes: AdminThemeRow[]) {
  return {
    getCandidatePairs: (isDark: boolean) =>
      allThemes.filter((theme) => theme.isDark !== isDark && !theme.pairId),
    getInitialIsDark: (lockedPairId: string | null | undefined) => {
      const lockedTheme = lockedPairId ? allThemes.find((t) => t.id === lockedPairId) : null;
      return lockedTheme ? !lockedTheme.isDark : undefined;
    },
  };
}

/**
 * Returns statistics for the font catalog.
 */
export function getAdminFontStats(fonts: AdminFontRow[]) {
  return {
    total: fonts.length,
    active: fonts.filter((f) => f.isActive).length,
  };
}

// ─── Back-office (#18) — users / audit pagination helpers ────────────────────

/** Default page size for the back-office user list + audit log.*/
export const ADMIN_PAGE_SIZE = 25;

export type AdminUserDTO = components["schemas"]["UserDTO"];

/**
 * Client-side filter for the user manager. The list endpoint returns every user;
 * we narrow by a case-insensitive email/name match and an exact role, then slice
 * the requested 1-based page. Returns the slice plus paging metadata so the UI
 * can render pager controls without re-deriving counts.
 */
export function filterAdminUsers(
  users: AdminUserDTO[],
  options: { q?: string; role?: string; page?: number; pageSize?: number },
): {
  rows: AdminUserDTO[];
  total: number;
  totalPages: number;
  page: number;
} {
  const q = options.q?.trim().toLowerCase() ?? "";
  const role = options.role?.trim() ?? "";
  const pageSize = options.pageSize ?? ADMIN_PAGE_SIZE;

  const matched = users.filter((u) => {
    const matchesQuery =
      q === "" || u.email.toLowerCase().includes(q) || u.name.toLowerCase().includes(q);
    const matchesRole = role === "" || u.role === role;
    return matchesQuery && matchesRole;
  });

  const total = matched.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(Math.max(1, options.page ?? 1), totalPages);
  const start = (page - 1) * pageSize;
  const rows = matched.slice(start, start + pageSize);

  return { rows, total, totalPages, page };
}

/** Converts a 1-based page into the `{ limit, offset }` the audit endpoint wants.*/
export function auditPageToRange(
  page: number | undefined,
  pageSize = ADMIN_PAGE_SIZE,
): { limit: number; offset: number } {
  const safePage = Math.max(1, page ?? 1);
  return { limit: pageSize, offset: (safePage - 1) * pageSize };
}
